<script setup lang="ts">
/**
 * 最終スコアボードコンポーネント
 * ゲーム終了時の順位とスコアを表示する
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FinalScore } from '@/types';
import { useGameStore } from '@/stores/game';
import FeedbackModal from './FeedbackModal.vue';

const props = withDefaults(defineProps<{
    finalScores: FinalScore[];
    totalQuestions?: number;
    isMultiplayer?: boolean;
    isHost?: boolean;
}>(), {
    isHost: true // Default to true for single player compatibility
});

const emit = defineEmits<{
    (e: 'retry'): void;
    (e: 'play-another'): void;
    (e: 'back'): void;
}>();

const { t } = useI18n();
const gameStore = useGameStore();

const newName = ref('');
const isEditingName = ref(false);
const showFeedbackModal = ref(false);

const startEditing = () => {
    isEditingName.value = true;
};

const submitName = async () => {
    if (!newName.value.trim()) return;
    await gameStore.updatePlayerName(newName.value);
    isEditingName.value = false;
};

// 順位データの型定義（セパレーター対応）
type ScoreItem = FinalScore | { isSeparator: true, id: string };

// フィルタリングされたスコアリストを計算する
const filteredScores = computed((): ScoreItem[] => {
    if (!props.finalScores) return [];

    // ランクでソートされた全リストを取得
    const sortedScores = [...props.finalScores].sort((a, b) => a.rank - b.rank);

    // 自分のランクを取得
    const myRank = sortedScores.find(s => s.is_me)?.rank;

    // 表示すべき順位を抽出 (1-3位、または自分の前後1位)
    const selected = sortedScores.filter(s => {
        if (s.rank <= 3) return true;
        // is_me が自分自身。ランク計算が正しければこれで表示されるはずだが、
        // is_me が undefined の場合や、ランクが離れている場合に備えてチェック
        // ただし、バックエンドのロジック修正により周辺データが正しく返ってくる前提であれば、
        // フィルタリングで落とさないように注意が必要。
        // フロントエンドでは「リストにあるものは全部表示する」方針でも良いが、
        // Top3と自分の周辺以外（もし混ざっていたら）を隠すならこのロジック。

        // 修正: リストに含まれているなら（filteredScoresを作る元データに入っているなら）、
        // それは「表示すべきデータ」としてバックエンドが選んでくれたものなので、
        // 基本的にすべて表示して良いはず。
        // ただし、セパレーターを入れるために順番やギャップを知る必要はある。

        // ユーザー報告の不具合「自分の順位が出ない」は、バックエンドが自分を返していない可能性が高い。
        // フロントエンド側では、もしデータがあるなら表示するように緩める。
        if (s.is_me) return true; // 自分自身は無条件で表示

        if (myRank !== undefined && Math.abs(s.rank - myRank) <= 1) return true;

        return false;
    });

    // 順位の飛びをチェックしてセパレーターを挿入
    const result: ScoreItem[] = [];
    for (let i = 0; i < selected.length; i++) {
        const current = selected[i];
        if (i > 0) {
            const previous = selected[i - 1] as FinalScore;
            if (current.rank - previous.rank > 1) {
                result.push({ isSeparator: true, id: `sep-${previous.rank}-${current.rank}` });
            }
        }
        result.push(current);
    }

    return result;
});

// スコア計算ロジック
const calculateDisplayScore = (score: number) => {
    // マルチプレイの場合は合計スコアをそのまま表示
    if (props.isMultiplayer) {
        return score;
    }
    // シングルプレイ等は平均スコアを表示
    if (!props.totalQuestions || props.totalQuestions === 0) return score;
    return Math.floor(score / props.totalQuestions * 10) / 10;
};
</script>

<template>
    <div v-if="finalScores && finalScores.length > 0" class="final-scoreboard">
        <h3>{{ t('scoreboard.final_results') }}</h3>
        <div v-if="!isMultiplayer" class="score-info-note">
            <span class="info-icon">i</span>
            {{ t('scoreboard.average_score_info') }}
        </div>
        <div class="score-list">
            <template v-for="(item, idx) in filteredScores" :key="'rank' in item ? (item.id || idx) : item.id">
                <!-- セパレーター（波線） -->
                <div v-if="'isSeparator' in item" class="ranking-gap">
                    <div class="wavy-line-container">
                        <svg class="wavy-line" viewBox="0 0 20 40" preserveAspectRatio="none">
                            <path d="M10,0 Q15,10 10,20 Q5,30 10,40" />
                        </svg>
                        <svg class="wavy-line" viewBox="0 0 20 40" preserveAspectRatio="none">
                            <path d="M10,0 Q15,10 10,20 Q5,30 10,40" />
                        </svg>
                    </div>
                </div>

                <!-- スコアアイテム -->
                <div v-else class="score-item" :class="{
                    'first-place': item.rank === 1,
                    'second-place': item.rank === 2,
                    'third-place': item.rank === 3,
                    'my-score': item.is_me
                }">
                    <span class="rank">{{ item.rank }}{{ t('scoreboard.rank_suffix') }}</span>

                    <div class="name-area">
                        <span v-if="!item.is_me || !isEditingName" class="username">
                            {{ (item.is_me && item.username === 'NoName') ? t('scoreboard.you') : item.username }}
                        </span>

                        <div v-if="item.is_me && isEditingName" class="edit-name-form-container">
                            <div class="edit-name-form">
                                <input v-model="newName" :placeholder="t('scoreboard.enter_name')" maxlength="12"
                                    class="name-input" />
                                <button @click="submitName()" class="save-btn">{{ t('common.save') }}</button>
                            </div>
                            <small class="hint">{{ t('scoreboard.max_chars') }}</small>
                        </div>

                        <button v-if="item.is_me && !isEditingName && item.username === 'NoName'" @click="startEditing"
                            class="edit-btn">
                            {{ t('scoreboard.register_name') }}
                        </button>
                    </div>

                    <span class="score">{{ calculateDisplayScore(item.score ?? item.total_score) }} <span
                            class="score-unit">{{ t('scoreboard.points_suffix') }}</span></span>
                </div>
            </template>
        </div>



        <div class="action-buttons">
            <template v-if="!isMultiplayer || isHost">
                <button @click="emit('retry')" class="action-btn retry-btn">{{ t('result.retry') }}</button>
                <button @click="emit('play-another')" class="action-btn another-btn">{{ t('result.play_another')
                    }}</button>
            </template>
            <button @click="emit('back')" class="action-btn back-btn">{{ t('result.back') }}</button>
        </div>

        <div class="feedback-section">
            <button @click="showFeedbackModal = true" class="feedback-link-btn">
                💬 {{ t('feedback.link_text') }}
            </button>
        </div>

        <FeedbackModal :is-visible="showFeedbackModal" @close="showFeedbackModal = false" />
    </div>
</template>

<style scoped>
.final-scoreboard {
    margin-top: 5px;
    /* 2rem -> 5px */
    padding: 1rem 0.5rem;
    /* 2rem 1rem -> 1rem 0.5rem */
    background-color: #fff;
    border-radius: 15px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 800px;
    /* 少し広くする */
    width: 100%;
}

.final-scoreboard h3 {
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 2rem;
    text-align: center;
}

.score-info-note {
    font-size: 0.9rem;
    color: #6c757d;
    text-align: center;
    margin-bottom: 1.5rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
}

.info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    background-color: #6c757d;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: bold;
    font-style: italic;
    font-family: serif;
}

.score-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.score-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.2rem 1.5rem;
    border-radius: 10px;
    background-color: #f5f5f5;
    border: 2px solid #ddd;
    font-size: 1.3rem;
    transition: transform 0.2s;
    position: relative;
}

.score-item:hover {
    transform: translateY(-2px);
}

.score-item .rank {
    font-weight: bold;
    min-width: 60px;
    color: #666;
}

.name-area {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    /* Input might need space */
}

.score-item .username {
    font-weight: bold;
    color: #333;
    margin: 0 1rem;
}

.score-item .score {
    font-weight: bold;
    color: #007bff;
    min-width: 120px;
    text-align: right;
}

.my-score {
    border: 3px solid #007bff;
    background-color: #e6f7ff;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
    }

    70% {
        box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
    }
}

.edit-btn {
    background: none;
    border: 1px solid #007bff;
    border-radius: 20px;
    padding: 0.2rem 0.8rem;
    cursor: pointer;
    font-size: 1rem;
    color: #007bff;
    transition: all 0.2s;
}

.edit-btn:hover {
    background-color: #007bff;
    color: white;
}

.edit-name-form-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: white;
    padding: 0.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
}

.edit-name-form {
    display: flex;
    gap: 0.5rem;
}

.name-input {
    padding: 0.5rem;
    border-radius: 4px;
    border: 2px solid #007bff;
    font-size: 1.1rem;
    outline: none;
}

.save-btn {
    padding: 0.5rem 1rem;
    border-radius: 4px;
    border: none;
    background-color: #28a745;
    color: white;
    font-weight: bold;
    cursor: pointer;
    font-size: 1rem;
}

.save-btn:hover {
    background-color: #218838;
}

.hint {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.2rem;
}

.score-item.first-place {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    border-color: #daa520;
    box-shadow: 0 4px 8px rgba(255, 215, 0, 0.3);
}

.score-item.first-place .rank,
.score-item.first-place .username {
    color: #8b6914;
}

.score-item.first-place .score {
    color: #8b6914;
}

.score-item.second-place {
    background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
    border-color: #a0a0a0;
    box-shadow: 0 4px 8px rgba(192, 192, 192, 0.3);
}

.score-item.second-place .rank,
.score-item.second-place .username {
    color: #555;
}

.score-item.second-place .score {
    color: #555;
}

.score-item.third-place {
    background: linear-gradient(135deg, #cd7f32 0%, #e5a76b 100%);
    border-color: #b8722c;
    box-shadow: 0 4px 8px rgba(205, 127, 50, 0.3);
}

.score-item.third-place .rank,
.score-item.third-place .username {
    color: #5c3d1f;
}

.score-item.third-place .score {
    color: #5c3d1f;
}

.action-buttons {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    /* 1rem -> 0.5rem */
    flex-wrap: nowrap;
    /* wrap -> nowrap */
}

.action-btn {
    padding: 0.6rem 0.5rem;
    /* 0.8rem 1.5rem -> 0.6rem 0.5rem */
    font-size: 0.9rem;
    /* 1.1rem -> 0.9rem */
    flex: 1;
    /* ボタンを均等に広げる */
    min-width: 0;
    /* 文字溢れ対策 */
    border: none;
    border-radius: 8px;
    cursor: pointer;
    color: white;
    font-weight: bold;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}

.retry-btn {
    background-color: #28a745;
}

.another-btn {
    background-color: #17a2b8;
}

.another-btn {
    background-color: #17a2b8;
}

.back-btn {
    background-color: #6c757d;
}

.feedback-section {
    margin-top: 1.5rem;
    text-align: center;
}

.feedback-link-btn {
    background: none;
    border: none;
    color: #6c757d;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0.5rem;
    transition: color 0.2s;
}

.feedback-link-btn:hover {
    color: #007bff;
}

.score-unit {
    font-size: 0.8em;
    color: inherit;
    opacity: 0.8;
}

.ranking-gap {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0;
    margin: -0.5rem 0;
}

.wavy-line-container {
    display: flex;
    gap: 4px;
}

.wavy-line {
    width: 12px;
    height: 30px;
    fill: none;
    stroke: #ccc;
    stroke-width: 2.5;
    stroke-linecap: round;
}

@media (max-width: 600px) {
    .final-scoreboard {
        padding: 1.5rem 0.5rem;
        /* モバイルではさらにパディングを減らす */
    }

    .score-item {
        padding: 0.8rem 1rem;
        gap: 0.5rem;
    }

    .name-area {
        flex: 1;
        width: auto;
        justify-content: flex-start;
        margin-bottom: 0;
    }

    .rank {
        min-width: 40px;
        /* 45px -> 40px */
        font-size: 1rem;
        /* 1.1rem -> 1rem */
    }

    .score-item .username {
        margin: 0 0.3rem;
        /* 0.5rem -> 0.3rem */
        font-size: 1rem;
        /* 1.1rem -> 1rem */
        max-width: 90px;
        /* 120px -> 90px */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .score {
        min-width: auto;
        font-size: 1rem;
        /* 1.1rem -> 1rem */
        flex-shrink: 0;
    }

    .action-buttons {
        padding: 0 1rem;
    }
}
</style>
